import { queryClient } from '@helpers';
import { ApiClientEnterprise } from '@helpers/apiClients';
import { Table } from '@molecules';
import { GridColDef } from '@mui/x-data-grid';
import { errorMessage, ITableEnterpriseUsers, successMessage } from '@services';
import { useCallback, useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import { useParams } from 'react-router-dom';
import { API_USERS_URL, DEFAULT_TOAST_MESSAGE_RESEND_REVOKE } from './constants';
import { IInvitationResponse, IResendRevokeInvitationData } from './types';
import { getColumns, getErrorMessage } from './utils';
import { AxiosError } from 'axios';
import QueryKeys from '@constants/query-keys';

const EnterpriseUsersTable = () => {
  const { id } = useParams();
  const [idInteract, setIdInteract] = useState('');

  const handleSuccess = () => {
    queryClient.refetchQueries(QueryKeys.enterpriseUsers).then(() => {
      successMessage(DEFAULT_TOAST_MESSAGE_RESEND_REVOKE.SUCCESS);
      setIdInteract('');
    });
  };

  const handleError = (error: AxiosError) => {
    const errorMsg = getErrorMessage(error.response?.data?.errorMessage);
    errorMessage(errorMsg);
    setIdInteract('');
  };

  const { mutate: resend, isLoading: isLoadingResend } = useMutation<
    IInvitationResponse,
    AxiosError,
    IResendRevokeInvitationData
  >((data) => ApiClientEnterprise.post(API_USERS_URL.RESEND, data), {
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const { mutate: revoke, isLoading: isLoadingRevoke } = useMutation<
    unknown,
    AxiosError,
    IResendRevokeInvitationData
  >((data) => ApiClientEnterprise.delete(API_USERS_URL.REVOKE, { data }), {
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const handleClickResend = useCallback(
    (data: IResendRevokeInvitationData, idSelected: string) => {
      if (data) {
        setIdInteract(idSelected);
        resend(data);
      }
    },
    [resend],
  );

  const handleClickRevoke = useCallback(
    (data: IResendRevokeInvitationData, idSelected: string) => {
      if (data) {
        setIdInteract(idSelected);
        revoke(data);
      }
    },
    [revoke],
  );

  const columns: GridColDef<ITableEnterpriseUsers>[] = useMemo(
    () =>
      getColumns({
        handleClickResend,
        handleClickRevoke,
        isLoadingResend,
        isLoadingRevoke,
        idInteract,
      }),
    [handleClickResend, handleClickRevoke, isLoadingResend, isLoadingRevoke, idInteract],
  );

  return (
    <Table<ITableEnterpriseUsers>
      queryName={QueryKeys.enterpriseUsers}
      columns={columns}
      apiRoute={`${API_USERS_URL.GET_LIST_USERS}`}
      withoutSearch
      usingIsFetching
      isEnterprise
      usingMidnightResponse
      preSelectedFilters={{
        organizationId: id,
      }}
    />
  );
};

export default EnterpriseUsersTable;
